import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { googleSignIn, signup, login, verifyEmail, logout } from '../features/auth/authSlice';
import {
  startMultipartUpload,
  completeMultipartUpload,
  scanContent,
  createJob,
} from '../features/contentCompliance/contentComplianceSlice';
import { createCheckout } from '../features/billing/billingSlice';
import { CREDIT_PACKS } from '../features/billing/constants';
import { track, setAnalyticsUser } from './analytics';

/**
 * Maps Redux actions to GA4 funnel events in one place, so every screen that
 * signs in, uploads, scans or checks out is covered without per-component
 * tracking calls. UI-only moments (scan finished, report viewed, pricing
 * viewed, share, purchase return) are tracked in their components.
 *
 * Event names follow GA4's recommended events where one exists (sign_up,
 * login, begin_checkout, purchase) so they light up GA's built-in reports.
 */
export const analyticsListener = createListenerMiddleware();

const accountType = (state) => (state.auth?.isAuthenticated ? 'signed_up' : 'anonymous');

analyticsListener.startListening({
  actionCreator: googleSignIn.fulfilled,
  effect: (action) => {
    const data = action.payload || {};
    setAnalyticsUser(data.user_id);
    // is_new_user is false when an anonymous visitor links Google, so those
    // conversions are counted as login. Known undercount of sign_up.
    track(data.is_new_user ? 'sign_up' : 'login', { method: 'google' });
  },
});

analyticsListener.startListening({
  actionCreator: signup.fulfilled,
  // No user_id yet: email accounts can't sign in until verified.
  effect: () => track('sign_up', { method: 'email' }),
});

analyticsListener.startListening({
  actionCreator: login.fulfilled,
  effect: (action) => {
    setAnalyticsUser(action.payload?.user_id);
    track('login', { method: 'email' });
  },
});

analyticsListener.startListening({
  actionCreator: verifyEmail.fulfilled,
  effect: (action) => {
    if (action.payload?.user_id) setAnalyticsUser(action.payload.user_id);
    track('email_verified');
  },
});

analyticsListener.startListening({
  actionCreator: logout,
  effect: () => setAnalyticsUser(null),
});

analyticsListener.startListening({
  actionCreator: startMultipartUpload.fulfilled,
  effect: (_action, api) => track('upload_started', { account_type: accountType(api.getState()) }),
});

analyticsListener.startListening({
  actionCreator: completeMultipartUpload.fulfilled,
  effect: (_action, api) => track('upload_completed', { account_type: accountType(api.getState()) }),
});

analyticsListener.startListening({
  matcher: isAnyOf(scanContent.fulfilled, createJob.fulfilled),
  effect: (action, api) =>
    track('scan_started', {
      platform: (action.meta?.arg?.platform || 'unknown').toLowerCase(),
      account_type: accountType(api.getState()),
    }),
});

analyticsListener.startListening({
  matcher: isAnyOf(scanContent.rejected, createJob.rejected),
  effect: (action) => {
    // toRejection() flattens these into flags on the payload.
    if (action.payload?.requiresTopup) {
      // Out of credits: the paywall moment the pricing work exists for.
      track('credit_wall_hit', { credits_required: action.payload.creditsRequired });
    } else if (action.payload?.requiresSignup) {
      // Anonymous visitor used up their free scans.
      track('anon_limit_hit');
    }
  },
});

analyticsListener.startListening({
  actionCreator: createCheckout.fulfilled,
  effect: (action) => {
    const pack = CREDIT_PACKS.find((p) => p.id === action.meta?.arg?.packId);
    track('begin_checkout', {
      currency: 'USD',
      value: pack ? pack.priceCents / 100 : undefined,
      items: pack ? [{ item_id: pack.id, item_name: `${pack.credits} credits`, price: pack.priceCents / 100, quantity: 1 }] : [],
    });
  },
});
